import { isHydraRoute } from '@/services/router/generate-path';
import type { MouseEvent } from 'react';
import { AppBundle, AppBundleRoutes } from './app-bundles';

// URLs within apps will be of the form /workspaces/1234/journeys or
// /workspaces/1234/pipelines etc.
// This function regurns a regex for matching a URL with a given app.
function workspaceAppRegex(app: AppBundle) {
  // eslint-disable-next-line local-rules/enforce-generate-path -- regex pattern, not a URL
  return new RegExp(`/workspaces/(last|(\\d+))${AppBundleRoutes[app]}`, 'i');
}

const WORKSPACE_SETTINGS_REGEX = /^\/workspaces\/(last|(\d+))\/settings/i;
const SETTINGS_REGEX = /^\/settings/i;
const TRIAL_EXPIRED_REGEX = /^\/trial-expired\/journeys$/;

// There are a few special URLs that don't follow the above pattern. This
// contains regexes for matching those special cases.
const SPECIAL_URLS: [RegExp, AppBundle][] = [
  [WORKSPACE_SETTINGS_REGEX, AppBundle.Journeys],
  [SETTINGS_REGEX, AppBundle.Journeys],
  [TRIAL_EXPIRED_REGEX, AppBundle.Journeys],
  [/^\/internal-tools/i, AppBundle.Journeys],
  [/^\/logout/i, AppBundle.Journeys],
];

export function getAppBundleFromUrl(url: string): AppBundle | null {
  if (isHydraRoute(url)) {
    return null; // Hydra-only URLs are not associated with any app bundle
  }

  // Check for special URLs
  const matchBySpecialUrl = SPECIAL_URLS.find(([regex]) => url.match(regex) != null);
  if (matchBySpecialUrl != null) {
    return matchBySpecialUrl[1];
  }

  return Object.values(AppBundle).find((app) => url.match(workspaceAppRegex(app)) != null) ?? null;
}

/**
 * Reload the current page. Wrapped in a function so call sites can be tested
 * without triggering a real browser navigation.
 */
export function reloadWindow() {
  window.location.reload();
}

export function shouldProcessLinkClick(event: MouseEvent) {
  const isTargetBlank = event.currentTarget instanceof HTMLAnchorElement && event.currentTarget.target === '_blank';
  return event.button === 0 && !isTargetBlank && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey;
}

export function workspaceRootUrl(workspaceId: number | null) {
  // eslint-disable-next-line local-rules/enforce-generate-path -- dynamic fallback URL with nullable workspace ID
  return `/workspaces/${workspaceId ?? 'last'}/journeys`;
}

/**
 * Given a workspace and the current URL path, compute the best URL to navigate
 * to in the new workspace. This works by replacing the workspace ID and then
 * walking up the path segments, stripping any trailing segments that look like
 * dynamic IDs (numeric values, UUIDs, etc.) so that the user lands on the
 * nearest parent listing/index page rather than always being dumped to the
 * dashboard.
 *
 * For example:
 *   /workspaces/123/journeys/segments/456       → /workspaces/789/journeys/segments
 *   /workspaces/123/journeys/campaigns/456/overview → /workspaces/789/journeys/campaigns
 *   /workspaces/123/journeys/segments            → /workspaces/789/journeys/segments
 *   /workspaces/123/pipelines/sources/456        → /workspaces/789/pipelines/sources
 */
export function workspaceUrlFromCurrentPath(workspaceId: number, currentPath: string): string {
  // Match /workspaces/:id/... pattern and replace the workspace ID
  const workspacePathMatch = currentPath.match(/^\/workspaces\/(last|\d+)(\/.*)?$/i);

  if (workspacePathMatch == null) {
    // Current path doesn't match workspace pattern, fall back to root
    return workspaceRootUrl(workspaceId);
  }

  const restOfPath = workspacePathMatch[2] ?? '';
  const segments = restOfPath.split('/').filter((s) => s.length > 0);

  // Walk up the parent routes: keep removing the last segment as long as any
  // remaining segment (after the app bundle) is a dynamic ID. This ensures
  // we don't land on a sub-route of a resource that doesn't exist in the
  // target workspace (e.g. /campaigns/456/overview → /campaigns).
  while (segments.length > 1 && segments.some((s, index) => index > 0 && isDynamicSegment(s, segments[index - 1]))) {
    segments.pop();
  }

  // If we stripped everything down to just the app bundle (e.g. "/journeys"),
  // fall back to the default workspace root rather than landing on a bare
  // app-bundle URL.
  if (segments.length <= 1) {
    return workspaceRootUrl(workspaceId);
  }

  // eslint-disable-next-line local-rules/enforce-generate-path -- dynamically constructed from path segments
  return `/workspaces/${workspaceId}/${segments.join('/')}`;
}

// Route segments where the immediately following segment is always a dynamic
// slug (e.g. /objects/accounts where "accounts" is a custom object type slug).
const DYNAMIC_CHILD_SEGMENTS = new Set(['objects']);

/**
 * Returns true if a path segment looks like a dynamic ID rather than a named
 * route segment. Any segment containing a digit is treated as dynamic, since
 * dynamic segments come in many forms: pure numeric IDs ("123"), UUIDs,
 * alphanumeric IDs ("ob0101f2b501"), or even named slugs that include numbers.
 *
 * Some route segments (like "objects") are known to always be followed by a
 * dynamic slug (e.g. the object type name), so their children are also treated
 * as dynamic even if they contain no digits.
 */
function isDynamicSegment(segment: string, previousSegment?: string): boolean {
  // Segments immediately after known parent routes are always dynamic slugs
  if (previousSegment != null && DYNAMIC_CHILD_SEGMENTS.has(previousSegment)) {
    return true;
  }

  // Any segment containing a digit (numeric IDs, alphanumeric IDs, slugs with numbers, etc.)
  if (/\d/.test(segment)) {
    return true;
  }

  // UUIDs can technically be all letters (e.g. "abcdefab-abcd-abcd-abcd-abcdefabcdef")
  if (/^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i.test(segment)) {
    return true;
  }

  return false;
}
